import { useState } from 'react';
import { apiGet, apiPatch, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { useFeatureCatalog } from '../lib/useFeatureCatalog';
import { useModal } from '../contexts/ModalContext';
import { FeatureMatrix } from '../components/FeatureMatrix';
import { PendingPlanChange } from '../components/PendingPlanChange';

/**
 * Admin control over LynkPulse plan pricing and copy (docs/administration.md §24.4.4).
 *
 * This is the only screen in the portal that changes **what customers are charged**,
 * and it is built around that fact:
 *
 * 1. **Amounts are entered in Stripe minor units, with the major-unit equivalent
 *    shown live beside the field.** An admin typing `999` sees "$9.99" before
 *    saving. Accepting major units and multiplying here would invite exactly the
 *    100x error this sub-project just removed from the backend.
 * 2. **A save is confirmed, naming the old and new amount.** Every other admin
 *    toggle in this portal is reversible and low-stakes; a wrong price bills real
 *    people.
 *
 * ── COP is TWO-decimal ────────────────────────────────────────────────────────
 *
 * COP minor units are **centavos**, not pesos — verified against Stripe on
 * 2026-09-26. So 19,900 pesos is `1990000`. The live echo beside each field is the
 * safeguard that makes this visible rather than a fact an admin has to remember:
 * typing `19900` into a COP field shows "$199" and the mistake is obvious before
 * saving.
 */

type PlanTier = 'free' | 'plus' | 'guardian';
type PlanCurrency = 'USD' | 'COP';
type BillingInterval = 'monthly' | 'yearly';

interface PlanPrice {
  amountMinor: number;
  currency: PlanCurrency;
  interval: BillingInterval;
}

interface PlanDefinition {
  tier: PlanTier;
  nameEn: string;
  nameEs: string;
  badgeEn: string;
  badgeEs: string;
  taglineEn: string;
  taglineEs: string;
  featuresEn: string[];
  featuresEs: string[];
  prices: PlanPrice[];
}

const CURRENCIES: PlanCurrency[] = ['USD', 'COP'];
const INTERVALS: BillingInterval[] = ['monthly', 'yearly'];

/** Display decimals per currency. Mirrors the backend's CURRENCY_CONFIG. */
const DISPLAY_DECIMALS: Record<PlanCurrency, number> = { USD: 2, COP: 0 };
const LOCALE: Record<PlanCurrency, string> = { USD: 'en-US', COP: 'es-CO' };

/**
 * Render a minor-unit amount as its major-unit equivalent.
 *
 * Divides by 100 for **every** currency — that is the whole invariant. Exported for
 * the test, because this function is the safeguard against the 100x error.
 */
export function majorUnitEcho(amountMinor: number, currency: PlanCurrency): string {
  if (!Number.isFinite(amountMinor)) return '—';
  const major = amountMinor / 100;
  try {
    const hasFraction = amountMinor % 100 !== 0;
    return new Intl.NumberFormat(LOCALE[currency], {
      style: 'currency',
      currency,
      // A whole-peso COP amount keeps its clean 0-decimal rendering. A fractional
      // one shows the fraction rather than rounding it away: an amountMinor that
      // is not a whole number of major units is almost always a typo, and hiding
      // it is how a 100x error reaches customers.
      minimumFractionDigits: hasFraction ? 2 : DISPLAY_DECIMALS[currency],
      maximumFractionDigits: hasFraction ? 2 : DISPLAY_DECIMALS[currency],
    })
      .format(major)
      .replace(/\u00A0/g, ' ');
  } catch {
    return `${major}`;
  }
}

/** A minor amount is valid only as a non-negative integer — Stripe's own rule. */
export function isValidMinorAmount(raw: string): boolean {
  if (!/^\d+$/.test(raw.trim())) return false;
  const n = Number(raw.trim());
  return Number.isSafeInteger(n) && n >= 0;
}

/** Key for the edit map — one price is identified by tier + currency + interval. */
const priceKey = (tier: PlanTier, currency: PlanCurrency, interval: BillingInterval) =>
  `${tier}:${currency}:${interval}`;

type PlansTab = 'pricing' | 'features';

export function PlansPage() {
  // Native window.confirm is forbidden in this portal — it renders an OS alert
  // that ignores the theme entirely. useModal() is the sanctioned path and now
  // renders the Pages v5 dialog (bundle/.../simply_white/modals.html).
  const modal = useModal();
  const {
    data: plans,
    loading,
    error,
    reload,
  } = useApiResource<PlanDefinition[]>(
    () => apiGet<{ plans: PlanDefinition[] }>('/admin/lynk-pulse/plans').then((r) => r.plans ?? []),
    [],
  );

  const [tab, setTab] = useState<PlansTab>('pricing');
  const {
    data: featureData,
    loading: featuresLoading,
    error: featuresError,
    reload: reloadFeatures,
  } = useFeatureCatalog();
  // Gates FeatureMatrix's derived "tier has no features" alarm. features=[]
  // means something different depending on WHY it's empty: a genuinely
  // unconfigured tier (real, alarm-worthy) vs. a load that hasn't finished or
  // failed outright (not a fact about the catalog at all). Only the former
  // should ever raise the alarm.
  const featuresLoaded = !featuresLoading && !featuresError && featureData !== null;

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // What the last save actually did for this tier. A price change may be
  // applied outright or parked awaiting approval of the new Terms, and
  // reporting "Saved" for the second would be a lie — the price has not moved.
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [saveOutcome, setSaveOutcome] = useState<'applied' | 'pending' | null>(null);

  const findPrice = (plan: PlanDefinition, currency: PlanCurrency, interval: BillingInterval) =>
    plan.prices.find((p) => p.currency === currency && p.interval === interval);

  /** Every price on this plan the admin has edited to a new, valid value. */
  const pendingChanges = (plan: PlanDefinition) =>
    CURRENCIES.flatMap((currency) =>
      INTERVALS.flatMap((interval) => {
        const price = findPrice(plan, currency, interval);
        if (!price) return [];
        const raw = drafts[priceKey(plan.tier, currency, interval)];
        if (raw === undefined || !isValidMinorAmount(raw)) return [];
        const next = Number(raw);
        if (next === price.amountMinor) return [];
        return [{ currency, interval, from: price.amountMinor, to: next }];
      }),
    );

  /** Any draft on this plan that is not a whole number — blocks the save. */
  const invalidDrafts = (plan: PlanDefinition) =>
    CURRENCIES.flatMap((currency) =>
      INTERVALS.flatMap((interval) => {
        const raw = drafts[priceKey(plan.tier, currency, interval)];
        return raw !== undefined && !isValidMinorAmount(raw) ? [raw] : [];
      }),
    );

  /**
   * One save per PLAN, not per field.
   *
   * Every edited price on the plan goes up in a single PATCH — the endpoint
   * already takes an array — behind a single confirmation that lists each
   * change. Eight Save buttons meant eight round trips, eight confirmations,
   * and a plan that could sit half-updated if the admin stopped partway.
   */
  const savePlan = async (plan: PlanDefinition) => {
    const bad = invalidDrafts(plan);
    if (bad.length > 0) {
      setActionError(
        `"${bad[0]}" is not a whole number of minor units. Enter cents for USD or centavos for COP — no decimal point.`,
      );
      return;
    }

    const changes = pendingChanges(plan);
    if (changes.length === 0) return;

    // Both amounts in major units, because that is the form an admin can
    // sanity-check. "999 -> 1099" means little; "$9.99 -> $10.99" does.
    const confirmed = await modal.confirm({
      title: `Change ${plan.nameEn} pricing`,
      message:
        changes
          .map(
            (c) =>
              `${c.interval} ${c.currency}: ${majorUnitEcho(c.from, c.currency)} (${c.from}) ` +
              `→ ${majorUnitEcho(c.to, c.currency)} (${c.to})`,
          )
          .join('\n') +
        '\n\nThis is what customers will be charged. If the change rewrites the Terms of ' +
        'Service it will be held for approval first, and the old price stays live until then.',
      confirmLabel: changes.length === 1 ? 'Change price' : `Change ${changes.length} prices`,
      danger: true,
    });
    if (!confirmed) return;

    setBusyKey(plan.tier);
    setActionError(null);
    setSavedKey(null);
    setSaveOutcome(null);
    try {
      const outcome = await apiPatch<{ applied?: boolean; pending?: boolean }>(
        `/admin/lynk-pulse/plans/${encodeURIComponent(plan.tier)}`,
        {
          prices: changes.map((c) => ({
            amountMinor: c.to,
            currency: c.currency,
            interval: c.interval,
          })),
        },
      );
      setSaveOutcome(outcome?.pending ? 'pending' : 'applied');
      // Drop only this plan's drafts, so an edit in progress on another plan
      // is not silently discarded by a save here.
      setDrafts((d) =>
        Object.fromEntries(Object.entries(d).filter(([k]) => !k.startsWith(`${plan.tier}:`))),
      );
      setSavedKey(plan.tier);
      reload();
    } catch (err) {
      // No optimistic update on this screen. A price that appears saved but was
      // rejected is the one failure mode worth a full round-trip to avoid.
      setActionError(
        err instanceof ApiError ? err.message : `Failed to update ${plan.tier} pricing`,
      );
    } finally {
      setBusyKey(null);
    }
  };

  const showLoadingState = !plans && loading;

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>LynkPulse Plans &amp; Pricing</h1>
          <p>
            What the Free, Plus and Guardian tiers cost and what they include. Prices are in{' '}
            <strong>Stripe minor units</strong> — cents for USD, <strong>centavos for COP</strong>,
            since COP is a two-decimal currency in Stripe. The large value beside each field is
            what the customer is charged; check it before saving.
          </p>
        </div>
        <button type="button" className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      {/* A price edit no longer takes effect on save — it is parked until the
          new Terms are approved. Showing that here means the admin who made
          the change sees why the price has not moved, without going hunting. */}
      <PendingPlanChange onResolved={reload} />

      <div className="tabs">
        <button
          type="button"
          className={tab === 'pricing' ? 'tab-btn active' : 'tab-btn'}
          aria-pressed={tab === 'pricing'}
          onClick={() => setTab('pricing')}
        >
          Pricing
        </button>
        <button
          type="button"
          className={tab === 'features' ? 'tab-btn active' : 'tab-btn'}
          aria-pressed={tab === 'features'}
          onClick={() => setTab('features')}
        >
          Features
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {actionError && (
        <div role="alert" className="error-banner">
          {actionError}
        </div>
      )}

      {tab === 'pricing' && showLoadingState && <p className="cell-sub">Loading plans…</p>}

      {tab === 'pricing' && (plans ?? []).map((plan) => (
        <div className="card plan-card" key={plan.tier}>
          <div className="section-header">
            <div>
              <h2 style={{ marginBottom: 0 }}>{plan.nameEn}</h2>
              <p className="cell-sub">
                {plan.tier} · {plan.taglineEn}
              </p>
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table dt-table">
              <thead>
                <tr>
                  <th>Interval</th>
                  {CURRENCIES.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {INTERVALS.map((interval) => (
                  <tr key={interval}>
                    <td>{interval}</td>
                    {CURRENCIES.map((currency) => {
                      const price = findPrice(plan, currency, interval);
                      if (!price) {
                        return (
                          <td key={currency}>
                            <span className="cell-sub">—</span>
                          </td>
                        );
                      }
                      const key = priceKey(plan.tier, currency, interval);
                      const draft = drafts[key];
                      const shown = draft ?? String(price.amountMinor);
                      const valid = isValidMinorAmount(shown);
                      const dirty = draft !== undefined && Number(draft) !== price.amountMinor;

                      return (
                        <td key={currency} className={dirty ? 'is-edited' : undefined}>
                          {/* The safeguard: the major-unit value, live, is the dominant
                              element — the raw minor-unit input is demoted beneath it. */}
                          <div className="plan-price-echo">
                            {valid ? majorUnitEcho(Number(shown), currency) : '—'}
                          </div>
                          {/* Pages v5 floating-label input — the kit's own markup
                              (bundle/.../simply_white/form_elements.html:566), so the
                              unit hint lives in the label instead of a caption below
                              and nothing here needs an inline style. */}
                          <div className="plan-price-field">
                            <div className="form-group form-group-default">
                              <label htmlFor={`price-${key}`}>
                                {valid ? 'Minor units' : 'Whole number only'}
                              </label>
                              <input
                                id={`price-${key}`}
                                className="form-control"
                                inputMode="numeric"
                                value={shown}
                                aria-label={`${plan.tier} ${interval} ${currency} amount in minor units`}
                                onChange={(e) =>
                                  setDrafts((d) => ({ ...d, [key]: e.target.value }))
                                }
                              />
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* One Save per plan, bottom-right — every edited price on this card
              goes up in a single PATCH behind a single confirmation. */}
          <div className="plan-card-footer">
            {savedKey === plan.tier && pendingChanges(plan).length === 0 && (
              <span className="cell-sub" data-testid={`save-outcome-${plan.tier}`}>
                {saveOutcome === 'pending'
                  ? 'Awaiting approval — the old price is still live'
                  : 'Saved'}
              </span>
            )}
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                busyKey === plan.tier ||
                invalidDrafts(plan).length > 0 ||
                pendingChanges(plan).length === 0
              }
              onClick={() => savePlan(plan)}
            >
              {busyKey === plan.tier
                ? 'Saving…'
                : pendingChanges(plan).length > 1
                  ? `Save ${pendingChanges(plan).length} changes`
                  : 'Save changes'}
            </button>
          </div>
        </div>
      ))}

      {tab === 'features' && featuresLoading && !featureData && (
        <p className="cell-sub">Loading features…</p>
      )}

      {tab === 'features' && featuresError && (
        <div className="error-banner" role="alert">
          {featuresError}
        </div>
      )}

      {tab === 'features' && (
        <FeatureMatrix
          features={featureData ?? []}
          loaded={featuresLoaded}
          onChanged={() => {
            reloadFeatures();
            reload();
          }}
        />
      )}
    </div>
  );
}

export default PlansPage;
